/**
 * Apply a 3x3 homography matrix to warp a source image onto an output canvas.
 *
 * For each pixel (x, y) in the output we compute the corresponding source
 * coordinate using the *inverse* of the supplied matrix (so `matrix` should
 * map source -> destination, i.e. the inverse homography that maps kilter ->
 * camera).  We then do nearest-neighbour sampling from the source image.
 *
 * @param {HTMLImageElement|HTMLCanvasElement} srcImage  - the source image
 * @param {number[][]} matrix  - 3x3 homography (source -> dest)
 * @param {number} dstWidth   - output width  (camera frame width)
 * @param {number} dstHeight  - output height (camera frame height)
 * @returns {HTMLCanvasElement} - canvas containing the warped image
 */
export default function warpPerspective(srcImage, matrix, dstWidth, dstHeight) {
  // --- read source pixels ---------------------------------------------------
  const srcCanvas = document.createElement("canvas")
  srcCanvas.width = srcImage.naturalWidth || srcImage.width
  srcCanvas.height = srcImage.naturalHeight || srcImage.height
  const srcCtx = srcCanvas.getContext("2d")
  srcCtx.drawImage(srcImage, 0, 0)
  const srcData = srcCtx.getImageData(0, 0, srcCanvas.width, srcCanvas.height)
  const srcPixels = srcData.data
  const srcW = srcCanvas.width
  const srcH = srcCanvas.height

  // --- invert the matrix (dest -> source) ------------------------------------
  const m = matrix
  const [a, b, c] = m[0]
  const [d, e, f] = m[1]
  const [g, h, i] = m[2]

  const det =
    a * (e * i - f * h) - b * (d * i - f * g) + c * (d * h - e * g)

  const inv = [
    [(e * i - f * h) / det, (c * h - b * i) / det, (b * f - c * e) / det],
    [(f * g - d * i) / det, (a * i - c * g) / det, (c * d - a * f) / det],
    [(d * h - e * g) / det, (b * g - a * h) / det, (a * e - b * d) / det],
  ]

  // --- allocate output -------------------------------------------------------
  const dstCanvas = document.createElement("canvas")
  dstCanvas.width = dstWidth
  dstCanvas.height = dstHeight
  const dstCtx = dstCanvas.getContext("2d")
  const dstImg = dstCtx.createImageData(dstWidth, dstHeight)
  const dstPixels = dstImg.data

  // --- warp (inverse mapping, nearest-neighbour) -----------------------------
  const [i00, i01, i02] = inv[0]
  const [i10, i11, i12] = inv[1]
  const [i20, i21, i22] = inv[2]

  for (let y = 0; y < dstHeight; y++) {
    for (let x = 0; x < dstWidth; x++) {
      const w = i20 * x + i21 * y + i22
      const sx = Math.round((i00 * x + i01 * y + i02) / w)
      const sy = Math.round((i10 * x + i11 * y + i12) / w)

      if (sx >= 0 && sx < srcW && sy >= 0 && sy < srcH) {
        const srcIdx = (sy * srcW + sx) * 4
        const dstIdx = (y * dstWidth + x) * 4
        dstPixels[dstIdx] = srcPixels[srcIdx]
        dstPixels[dstIdx + 1] = srcPixels[srcIdx + 1]
        dstPixels[dstIdx + 2] = srcPixels[srcIdx + 2]
        dstPixels[dstIdx + 3] = srcPixels[srcIdx + 3]
      }
    }
  }

  dstCtx.putImageData(dstImg, 0, 0)
  return dstCanvas
}
